import React from 'react';
import PageLayout from '../components/layout/PageLayout';
import HelmetWrapper from '../components/common/HelmetWrapper';

const PrivacyPolicy = () => {
  return (
    <PageLayout>
      <HelmetWrapper>
        <title>Privacy Policy - PakPoints</title>
        <meta name="description" content="Privacy Policy for PakPoints - Learn how we collect, use, and protect your personal information." />
        <meta property="og:title" content="Privacy Policy - PakPoints" />
        <meta property="og:description" content="Privacy Policy for PakPoints - Learn how we collect, use, and protect your personal information." />
      </HelmetWrapper>

      <div className="bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
            
            <div className="prose max-w-none">
              <p className="text-gray-600 mb-6">
                <strong>Last updated:</strong> {new Date().toLocaleDateString()}
              </p>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
                <p className="text-gray-700 mb-4">
                  Welcome to PakPoints ("we," "our," or "us"). This Privacy Policy explains how we collect, 
                  use, disclose, and safeguard your information when you visit our website pakpoints.com 
                  (the "Site"). Please read this privacy policy carefully.
                </p>
                <p className="text-gray-700 mb-4">
                  By using our Site, you agree to the collection and use of information in accordance 
                  with this policy. If you do not agree with the terms of this Privacy Policy, 
                  please do not access the Site.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3">2.1 Personal Information</h3>
                <p className="text-gray-700 mb-4">
                  We may collect personal information that you voluntarily provide to us when you:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                  <li>Register for an account</li>
                  <li>Create listings for societies, businesses, or properties</li>
                  <li>Post content, reviews, or comments</li>
                  <li>Contact us or communicate with us</li>
                  <li>Subscribe to our newsletter</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">2.2 Information Collected Automatically</h3>
                <p className="text-gray-700 mb-4">
                  We automatically collect certain information when you visit our Site, including:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                  <li>IP address and location data</li>
                  <li>Browser type and version</li>
                  <li>Operating system</li>
                  <li>Pages visited and time spent on pages</li>
                  <li>Referring website</li>
                  <li>Device information</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
                <p className="text-gray-700 mb-4">
                  We use the information we collect to:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                  <li>Provide, operate, and maintain our Site</li>
                  <li>Improve, personalize, and expand our Site</li>
                  <li>Understand and analyze how you use our Site</li>
                  <li>Develop new products, services, features, and functionality</li>
                  <li>Communicate with you for customer service and support</li>
                  <li>Send you marketing and promotional communications</li>
                  <li>Process transactions and send related information</li>
                  <li>Find and prevent fraud</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Information Sharing and Disclosure</h2>
                <p className="text-gray-700 mb-4">
                  We may share your information in the following situations:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                  <li><strong>With your consent:</strong> We may share your information with your explicit consent</li>
                  <li><strong>Service providers:</strong> We may share information with third-party service providers who assist us in operating our Site</li>
                  <li><strong>Legal requirements:</strong> We may disclose information if required by law or to protect our rights</li>
                  <li><strong>Business transfers:</strong> We may share information in connection with a merger, acquisition, or sale of assets</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Cookies and Tracking Technologies</h2>
                <p className="text-gray-700 mb-4">
                  We use cookies and similar tracking technologies to track activity on our Site and store certain information. 
                  You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
                </p>
                <p className="text-gray-700 mb-4">
                  We use cookies for:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                  <li>Authentication and security</li>
                  <li>Personalization and user preferences</li>
                  <li>Analytics and performance monitoring</li>
                  <li>Advertising and marketing</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Google AdSense</h2>
                <p className="text-gray-700 mb-4">
                  Our Site uses Google AdSense, a web advertising service provided by Google. Google AdSense uses cookies 
                  to serve ads based on your visits to our Site and other sites on the Internet.
                </p>
                <p className="text-gray-700 mb-4">
                  You may opt out of personalized advertising by visiting 
                  <a href="https://www.google.com/settings/ads" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    Google Ad Settings
                  </a>.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Data Security</h2>
                <p className="text-gray-700 mb-4">
                  We implement appropriate security measures to protect your personal information against unauthorized access, 
                  alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic 
                  storage is 100% secure.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Your Rights</h2>
                <p className="text-gray-700 mb-4">
                  Depending on your location, you may have certain rights regarding your personal information, including:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                  <li>The right to access your personal information</li>
                  <li>The right to correct inaccurate information</li>
                  <li>The right to delete your personal information</li>
                  <li>The right to restrict processing of your information</li>
                  <li>The right to data portability</li>
                  <li>The right to object to processing</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
                <p className="text-gray-700 mb-4">
                  Our Site is not intended for children under 13 years of age. We do not knowingly collect personal 
                  information from children under 13. If you are a parent or guardian and believe your child has 
                  provided us with personal information, please contact us.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Changes to This Privacy Policy</h2>
                <p className="text-gray-700 mb-4">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting 
                  the new Privacy Policy on this page and updating the "Last updated" date.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Contact Us</h2>
                <p className="text-gray-700 mb-4">
                  If you have any questions about this Privacy Policy, please contact us:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 mb-2">
                    <strong>Email:</strong> privacy@pakpoints.com
                  </p>
                  <p className="text-gray-700 mb-2">
                    <strong>Website:</strong> <a href="https://www.pakpoints.com" className="text-blue-600 hover:underline">www.pakpoints.com</a>
                  </p>
                  <p className="text-gray-700">
                    <strong>Address:</strong> Pakistan
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default PrivacyPolicy;
